/**
 * FileInspector — ADR-177 D4, opening the file a phase names.
 *
 * TWO ASKS, ONE CAPABILITY. Driving a real run on spartan produced both within a minute
 * of each other:
 *
 *   "on the output of Run design [the change] can't be seen it just references it …
 *    we would need to be able to inspect the yaml. with what is changed like a diff."
 *   "We should also be able to open and inspect any referenced python that is called out."
 *
 * Both are "let me read the file this line is talking about". The tree was naming paths —
 * `agents/analytics/activities/weekly-health.yaml`, `drive_upload.py`,
 * `toolchain/activity-commands.yaml` — and a path with no way to open it is a reference,
 * not evidence. ADR-166 D5 makes a citation the difference between a constraint and an
 * assertion; a citation nobody can follow is back to an assertion.
 *
 * WHAT IT SHOWS, and the distinction is the point:
 *
 *   - a file the run AUTHORED, against the published one: a diff, because "what changed"
 *     is the question and a wall of unchanged YAML buries it;
 *   - a file the run authored that does not exist yet (a new test): its content, marked
 *     as new — not an empty diff, which reads as "nothing changed";
 *   - a file the run only CITED: its content at the cited line.
 *
 * Collapsed by default. A run's tree is a summary and stays scannable; opening a file is
 * a deliberate act.
 */
import { useCallback, useEffect, useState } from 'react'

import { api } from '../api/client'

/** A minimal line diff — enough to answer "what changed", not a merge tool.
 *
 * Written here rather than pulled in: the frontend has no diff dependency, the inputs are
 * two YAML files of a few hundred lines, and adding a library to this bundle (already
 * over the 500kB warning) to render a side panel is a poor trade. A longest-common-
 * subsequence over LINES is exact for that job.
 */
export function diffLines(before: string, after: string): { kind: ' ' | '-' | '+'; text: string }[] {
  const a = before.split('\n')
  const b = after.split('\n')
  // LCS table. Bounded deliberately: an activity is a few hundred lines, and a quadratic
  // table over two 20k-line files would lock the tab. Past the bound the honest answer is
  // "too large to diff", which the caller renders as the file itself.
  const n = a.length
  const m = b.length
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }
  const out: { kind: ' ' | '-' | '+'; text: string }[] = []
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ kind: ' ', text: a[i] }); i++; j++ } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: '-', text: a[i] }); i++
    } else {
      out.push({ kind: '+', text: b[j] }); j++
    }
  }
  while (i < n) { out.push({ kind: '-', text: a[i] }); i++ }
  while (j < m) { out.push({ kind: '+', text: b[j] }); j++ }
  return out
}

/** Lines either side of a change to keep. A diff with no context cannot be placed in the
 *  file; a diff with all of it is the file. */
const CONTEXT = 3

/** Drops runs of unchanged lines longer than 2*CONTEXT, leaving a marker. */
export function collapseUnchanged(
  rows: { kind: ' ' | '-' | '+'; text: string }[],
): { kind: ' ' | '-' | '+' | '@'; text: string }[] {
  const keep = new Array<boolean>(rows.length).fill(false)
  rows.forEach((r, idx) => {
    if (r.kind === ' ') return
    for (let k = Math.max(0, idx - CONTEXT); k <= Math.min(rows.length - 1, idx + CONTEXT); k++) keep[k] = true
  })
  const out: { kind: ' ' | '-' | '+' | '@'; text: string }[] = []
  let skipped = 0
  rows.forEach((r, idx) => {
    if (keep[idx]) {
      if (skipped > 0) {
        out.push({ kind: '@', text: `… ${skipped} unchanged line${skipped === 1 ? '' : 's'}` })
        skipped = 0
      }
      out.push(r)
    } else {
      skipped++
    }
  })
  if (skipped > 0) out.push({ kind: '@', text: `… ${skipped} unchanged line${skipped === 1 ? '' : 's'}` })
  return out
}

/** The maximum file this will diff. Past it, the content is shown instead — an honest
 *  "too large to diff" beats a locked tab. */
const MAX_DIFF_CHARS = 400_000

function DiffBody({ before, after }: { before: string; after: string }) {
  if (before.length + after.length > MAX_DIFF_CHARS) {
    return (
      <pre className="max-h-96 overflow-auto bg-gray-50 p-2 text-[10px] leading-relaxed">
        <div className="mb-1 text-amber-800">
          Too large to diff — showing the authored file.
        </div>
        {after}
      </pre>
    )
  }
  const rows = collapseUnchanged(diffLines(before, after))
  const added = rows.filter(r => r.kind === '+').length
  const removed = rows.filter(r => r.kind === '-').length
  return (
    <div className="max-h-96 overflow-auto bg-gray-50 text-[10px] leading-relaxed">
      <div className="sticky top-0 bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700">
        <span className="text-emerald-700">+{added}</span>{' '}
        <span className="text-red-700">−{removed}</span>
        {added === 0 && removed === 0 && <span className="ml-1 text-gray-600">— identical to the published file</span>}
      </div>
      <pre className="p-2">
        {rows.map((r, k) => (
          <div key={k} className={
            r.kind === '+' ? 'bg-emerald-50 text-emerald-900'
              : r.kind === '-' ? 'bg-red-50 text-red-900'
                : r.kind === '@' ? 'text-gray-400 italic'
                  : 'text-gray-700'}>
            {r.kind === '@' ? r.text : `${r.kind} ${r.text}`}
          </div>
        ))}
      </pre>
    </div>
  )
}

/**
 * One openable file.
 *
 * `authored` is the content this run produced, when it produced any. With it, the panel
 * diffs against the published file; without it, the panel just reads the file — which is
 * the citation case.
 */
export function FileInspector({ path, authored, line }: {
  path: string
  authored?: string
  /** The cited line, scrolled to and marked. A citation reads `file.yaml:200-222`. */
  line?: number
}) {
  const [open, setOpen] = useState(false)
  const [published, setPublished] = useState<string | null>(null)
  const [found, setFound] = useState<boolean | null>(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await api.fleetFile(path)
      setPublished(r.content ?? '')
      setFound(!!r.found)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'the file could not be read'
      // A 404 IS AN ANSWER, not a failure: a run authors files that do not exist yet, and
      // reporting "new file" as an error would make every authored test look broken.
      if (/404|not found/i.test(msg)) {
        setFound(false)
        setPublished('')
      } else {
        setErr(msg)
      }
    } finally {
      setLoading(false)
    }
  }, [path])

  useEffect(() => { if (open && published === null && !err) void load() }, [open, published, err, load])

  return (
    <div className="mt-0.5">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-[10px] text-indigo-700 hover:underline">
        {open ? '▾ hide' : '▸ open'} {authored !== undefined ? 'the change' : 'this file'}
      </button>

      {open && loading && <div className="text-[10px] text-gray-500">reading {path}…</div>}

      {open && err && (
        <div className="rounded border border-red-300 bg-red-50 p-1.5 text-[10px] text-red-800">
          Could not read <span className="font-mono">{path}</span> — {err}
          <button type="button" onClick={() => void load()}
            className="ml-2 rounded border border-red-300 bg-white px-1.5 hover:bg-red-100">Retry</button>
        </div>
      )}

      {open && !loading && !err && published !== null && (
        <div className="mt-1 rounded border border-gray-200">
          {authored !== undefined ? (
            found ? <DiffBody before={published} after={authored} /> : (
              <>
                <div className="bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-900">
                  New file — it does not exist in the fleet yet.
                </div>
                <pre className="max-h-96 overflow-auto bg-gray-50 p-2 text-[10px] leading-relaxed">{authored}</pre>
              </>
            )
          ) : found ? (
            <CitedFile content={published} line={line} />
          ) : (
            <div className="bg-amber-50 px-2 py-1 text-[10px] text-amber-900">
              <span className="font-mono">{path}</span> is not in this fleet&apos;s overlay.
              A claim citing it is citing something that is not here.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** A cited file, opened at the cited line rather than at the top. */
function CitedFile({ content, line }: { content: string; line?: number }) {
  const lines = content.split('\n')
  // A window, not the whole file. A citation points at a place, and dropping 900 lines of
  // context around it is how "open the file" becomes "search the file".
  const WINDOW = 40
  const centre = typeof line === 'number' && line > 0 ? line : 1
  const from = Math.max(1, centre - Math.floor(WINDOW / 2))
  const to = Math.min(lines.length, from + WINDOW - 1)
  const slice = lines.slice(from - 1, to)
  return (
    <div className="max-h-96 overflow-auto bg-gray-50 text-[10px] leading-relaxed">
      <div className="sticky top-0 bg-gray-100 px-2 py-0.5 text-gray-700">
        lines {from}–{to} of {lines.length}
      </div>
      <pre className="p-2">
        {slice.map((ln, k) => {
          const no = from + k
          return (
            <div key={no} className={no === centre && typeof line === 'number'
              ? 'bg-amber-100 text-gray-900' : 'text-gray-700'}>
              <span className="mr-2 inline-block w-10 select-none text-right text-gray-400">{no}</span>
              {ln}
            </div>
          )
        })}
      </pre>
    </div>
  )
}

/** Splits `path/to/file.yaml:200-222` into its path and first line.
 *
 * Returns line `undefined` for a citation with no line — a bare path is a legitimate
 * citation and must not resolve to line 0, which would open every such file at its top
 * with a highlight on nothing. */
export function parseCitation(citation: string): { path: string; line?: number } {
  const m = /^(.*?):(\d+)(?:-\d+)?$/.exec(citation.trim())
  if (!m) return { path: citation.trim() }
  return { path: m[1], line: Number(m[2]) }
}
